import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/http';
import type { ErpListPrice } from '@/features/pricing/api/pricing';

export interface ProductSummary {
  id: string;
  sku: string;
  name: string;
  /** Capa de um anúncio do produto, na plataforma; nula = nenhum anúncio com foto. */
  coverUrl: string | null;
  listingCount: number;
  connectionCount: number;
  availableStock: number;
  /** Ausentes para quem não tem acesso a dados financeiros. */
  acquisitionCost?: string | null;
  /** Onde o produto está anunciado, cada canal com o seu lucro (ADR-058). */
  channels: ProductChannel[];
  /** Fora do portfólio desde (ADR-042); nulo = ativo. */
  discontinuedAt: string | null;
  acquisitionCostSource?: 'MANUAL' | 'IMPORT' | 'ERP' | null;
  acquisitionCostUpdatedAt?: string | null;
}

/** Um canal de venda do produto: a margem é de cada canal, nunca do produto (ADR-058). */
export interface ProductChannel {
  provider: string;
  listings: number;
  /** Pior anúncio que está vendendo no canal; `null` = nada analisado; ausente sem financeiro. */
  profitability?: ProductProfitability | null;
}

export interface ProductListing {
  id: string;
  connectionName: string;
  /** Plataforma do anúncio. */
  provider: string;
  externalId: string;
  externalVariationId: string;
  title: string;
  status: string;
  price: string;
  /** Preço normal quando em promoção na plataforma (ADR-071). */
  regularPrice: string | null;
  /** O preço que a lista do ERP manda para este anúncio (ADR-071). */
  erpListPrice: ErpListPrice | null;
  availableStock: number;
  fulfillmentStock: number | null;
  weightKg: string | null;
  heightCm: string | null;
  widthCm: string | null;
  lengthCm: string | null;
  pictureCount: number | null;
  coverUrl: string | null;
  permalink: string | null;
  listingType: string | null;
  missingSince: string | null;
  /** Vitrine de catálogo da plataforma (ADR-044). */
  catalogListing: boolean;
  /** No catálogo em par: o tradicional com que divide preço e estoque. */
  catalogOf: string | null;
  /** Ausente para quem não vê financeiro; `null` antes da primeira análise. */
  analysis?: ListingAnalysisSummary | null;
}

export interface MissingItem {
  code: string;
  label: string;
  category: string;
  reason: string;
}

/**
 * Leitura rápida do lucro no preço atual (regra no servidor, `profit-level.ts`): prejuízo, abaixo
 * da margem mínima, lucrativo, na margem desejada ou acima — ou sem custo, quando não há margem.
 */
export type ProfitLevel = 'LOSS' | 'LOW' | 'GOOD' | 'GREAT' | 'NO_COST';

export interface ProfitView {
  level: ProfitLevel;
  /** A conta saiu sem algum custo: a margem é um teto, a real é essa ou menor. */
  provisional: boolean;
  /** O que não entrou na conta ("Embalagem"). */
  pending: string[];
}

export const PROFIT_LEVEL_LABELS: Record<ProfitLevel, string> = {
  LOSS: 'Prejuízo',
  LOW: 'Margem baixa',
  GOOD: 'Lucrativo',
  GREAT: 'Acima da meta',
  NO_COST: 'Sem custo',
};

export interface ProductProfitability extends ProfitView {
  /** Pior anúncio que está vendendo: margem (fração) e lucro por venda. */
  margin: string;
  profit: string;
  highestMargin: string;
  listings: number;
}

export interface ListingAnalysisSummary extends ProfitView {
  status: 'COMPLETE' | 'INCOMPLETE';
  netProfit: string;
  netMargin: string;
  breakEvenPrice: string | null;
  suggestedPrice: string | null;
  commercialPrice: string | null;
  missing: MissingItem[];
  analyzedAt: string;
  /** A conta no preço atual, linha a linha — a mesma da calculadora. */
  evaluation: Evaluation;
  /** Desconto máximo sem prejuízo (fração); negativo = já no prejuízo; nulo sem equilíbrio. */
  maxDiscountRate: string | null;
}

export interface EvaluationLine {
  code: string;
  label: string;
  category: string;
  kind: 'FIXED_PER_UNIT' | 'PERCENT_OF_PRICE';
  rate?: string;
  amount: string;
  source: { type: string; reference?: string };
}

export interface Evaluation {
  price: string;
  totalCosts: string;
  netProfit: string;
  netMargin: string;
  grossMargin: string;
  markup: string | null;
  components: EvaluationLine[];
}

export interface SimulationResult {
  missing: MissingItem[];
  byPrice?: {
    status: string;
    evaluation: Evaluation;
    breakEvenPrice: string | null;
    maxDiscountRate: string | null;
  };
  byMargin?: {
    status: string;
    targetMargin: string;
    suggested: Evaluation;
    breakEvenPrice: string;
    commercial: { rounding: string; evaluation: Evaluation; belowTarget: boolean };
  };
}

export function simulate(
  listingId: string,
  body: { price?: string; targetMargin?: string },
): Promise<SimulationResult> {
  return apiRequest(`/analysis/listings/${listingId}/simulate`, { method: 'POST', body });
}

export type ProductActionKind = 'PAUSE' | 'RESUME' | 'DISCONTINUE';

export interface ProductActionStep {
  kind: 'LISTING_STATE' | 'ERP_DEACTIVATE';
  target?: 'ACTIVE' | 'PAUSED' | 'CLOSED';
  label: string;
  externalId: string;
  status: 'PENDING' | 'DONE' | 'SIMULATED' | 'MANUAL' | 'SKIPPED' | 'FAILED';
  detail?: string;
}

export interface ProductActionView {
  id: string;
  kind: ProductActionKind;
  status: 'PENDING' | 'RUNNING' | 'DONE' | 'PARTIAL';
  deactivateInErp: boolean;
  steps: ProductActionStep[];
  createdAt: string;
  finishedAt: string | null;
}

/** O que a ação vai fazer em cada loja e no ERP, antes de fazer. */
export interface ProductActionPlan {
  kind: ProductActionKind;
  channelWriteMode: 'disabled' | 'sandbox' | 'production';
  erpWriteMode: 'disabled' | 'production';
  products: {
    id: string;
    sku: string;
    name: string;
    alreadyDiscontinued: boolean;
    steps: ProductActionStep[];
    manual: string[];
  }[];
}

export const PRODUCT_ACTION_LABELS: Record<ProductActionKind, string> = {
  PAUSE: 'Pausar',
  RESUME: 'Retomar',
  DISCONTINUE: 'Descontinuar',
};

export interface ProductDetail extends ProductSummary {
  listings: ProductListing[];
  /** Pedidos de pausar, retomar e descontinuar, do mais recente (até 5). */
  actions: ProductActionView[];
  /** O que o ERP informou deste SKU; nulo = sem ERP ou SKU fora dele. */
  erp: {
    heightCm: string | null;
    widthCm: string | null;
    lengthCm: string | null;
    weightKg: string | null;
    availableStock: number | null;
    syncedAt: string | null;
    /** Como foi achado: SKU, SKU_SEM_PONTUACAO ou GTIN (ADR-036). */
    matchedBy: 'SKU' | 'SKU_SEM_PONTUACAO' | 'GTIN' | null;
    /** O nome do item no ERP, como está lá; o do produto é o do anúncio (ADR-058). */
    name: string | null;
  } | null;
}

export interface ProductDetailWithPackage extends ProductDetail {
  packageId?: string | null;
}

export interface ProductPage {
  items: ProductSummary[];
  total: number;
  page: number;
  pageSize: number;
  /**
   * Produtos em cada faixa de lucro no canal pedido, com a busca aplicada; só para quem vê
   * financeiro e com canal.
   */
  profitCounts?: Record<ProfitLevel | 'PROVISIONAL', number>;
  /** Com ERP ligado: produtos dos anúncios que não existem no ERP (fora do catálogo). */
  outsideErp?: number;
}

/** Filtro por faixa de lucro do catálogo; `provisional` = só os de conta incompleta. */
export interface ProfitFilter {
  /** Plataforma da faixa de lucro e da ordem por margem: o lucro é de cada canal (ADR-058). */
  channel?: string;
  level?: ProfitLevel;
  provisional?: boolean;
  /** Os descontinuados, em vez do portfólio. */
  discontinued?: boolean;
  /** Situação nas lojas; ausente = com estoque (o padrão do servidor). */
  situation?: CatalogSituation;
}

/** Com estoque (padrão), fora de estoque, inativos (nenhum anúncio ativo) ou todos. */
export type CatalogSituation = 'AVAILABLE' | 'OUT_OF_STOCK' | 'INACTIVE' | 'ALL';

export const productKeys = {
  all: ['products'] as const,
  list: (search: string, page: number, sort: ProductSort, filter: ProfitFilter = {}) =>
    [
      ...productKeys.all,
      'list',
      search,
      page,
      sort.column,
      sort.dir,
      filter.channel ?? '',
      filter.level ?? '',
      filter.provisional ?? false,
      filter.discontinued ?? false,
      filter.situation ?? 'AVAILABLE',
    ] as const,
  detail: (id: string) => [...productKeys.all, 'detail', id] as const,
};

/** Colunas que o servidor ordena (a lista é paginada: a ordem vale para o catálogo inteiro). */
export type ProductSortColumn = 'sku' | 'name' | 'listings' | 'stores' | 'stock' | 'cost';
export interface ProductSort {
  column: ProductSortColumn;
  dir: 'asc' | 'desc';
}

export function useProducts(
  search: string,
  page: number,
  sort: ProductSort,
  filter: ProfitFilter = {},
) {
  return useQuery({
    queryKey: productKeys.list(search, page, sort, filter),
    queryFn: () =>
      apiRequest<ProductPage>(
        `/products?${new URLSearchParams({
          search,
          page: String(page),
          pageSize: '25',
          sort: sort.column,
          dir: sort.dir,
          ...(filter.channel ? { channel: filter.channel } : {}),
          ...(filter.level ? { profit: filter.level } : {}),
          ...(filter.provisional ? { provisional: 'true' } : {}),
          ...(filter.discontinued ? { discontinued: 'true' } : {}),
          ...(filter.situation ? { situation: filter.situation } : {}),
        }).toString()}`,
      ),
    placeholderData: keepPreviousData,
  });
}

export function useProduct(id: string) {
  return useQuery({
    queryKey: productKeys.detail(id),
    queryFn: () => apiRequest<ProductDetail>(`/products/${id}`),
    // Pedido às lojas em andamento: atualiza sozinho até terminar.
    refetchInterval: (query) => {
      const status = query.state.data?.actions[0]?.status;
      return status === 'PENDING' || status === 'RUNNING' ? 3_000 : false;
    },
  });
}

export function useUpdateCost(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (acquisitionCost: string | null) =>
      apiRequest<void>(`/products/${id}/cost`, { method: 'PATCH', body: { acquisitionCost } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: productKeys.all }),
  });
}

export interface ProductActionRequest {
  kind: ProductActionKind;
  productIds: string[];
  deactivateInErp?: boolean;
}

/**
 * Prévia da ação. Consulta, e não mutação: só lê — o POST é porque a lista de produtos não cabe
 * bem na URL. Sem cache: cada abertura mostra o estado de agora.
 */
export function useProductActionPlan(body: ProductActionRequest) {
  return useQuery({
    queryKey: [
      ...productKeys.all,
      'action-plan',
      body.kind,
      body.productIds.join(','),
      body.deactivateInErp ?? false,
    ],
    queryFn: () =>
      apiRequest<ProductActionPlan>('/products/actions/preview', { method: 'POST', body }),
    gcTime: 0,
  });
}

export function useProductAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ProductActionRequest) =>
      apiRequest<{ actionIds: string[] }>('/products/actions', { method: 'POST', body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: productKeys.all }),
  });
}

export function useReactivateProduct(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiRequest<void>(`/products/${id}/reactivate`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: productKeys.all }),
  });
}
